import ComputerPlayer from './ComputerPlayer.js';

// The four central cells of the 8×8 board (rows 3–4, cols 3–4)
const CENTRAL_CELLS = new Set([27, 28, 35, 36]);

/**
 * Returns the orthogonally adjacent cell indices within the 8×8 board.
 * @param {number} index - Cell index (0–63)
 * @returns {number[]}
 */
const getAdjacentCells = (index) => {
	const row = Math.floor(index / 8);
	const col = index % 8;
	const adjacent = [];
	if (row > 0) adjacent.push(index - 8);
	if (row < 7) adjacent.push(index + 8);
	if (col > 0) adjacent.push(index - 1);
	if (col < 7) adjacent.push(index + 1);
	return adjacent;
};

/**
 * Smart computer player for the Cats and Dogs game.
 * Extends the generic ComputerPlayer interface with game-specific logic.
 */
class CatsAndDogsComputerPlayer extends ComputerPlayer {
	/**
	 * Returns true if the given player has not yet placed any piece on the board.
	 * @param {Array} board
	 * @param {number} player - 1 (cats) or 2 (dogs)
	 * @returns {boolean}
	 */
	isFirstMove(board, player) {
		const piece = player === 1 ? 'cat' : 'dog';
		return !board.some(cell => cell === piece);
	}

	/**
	 * Checks whether placing at `index` is a valid move for `player`.
	 * @param {number} index
	 * @param {number} player
	 * @param {Array} board
	 * @returns {boolean}
	 */
	isValidMoveFor(index, player, board) {
		if (board[index] !== null) return false;

		if (this.isFirstMove(board, player)) {
			if (player === 1 && !CENTRAL_CELLS.has(index)) return false;
			if (player === 2 && CENTRAL_CELLS.has(index)) return false;
		}

		const opponentPiece = player === 1 ? 'dog' : 'cat';
		for (const adj of getAdjacentCells(index)) {
			if (board[adj] === opponentPiece) return false;
		}
		return true;
	}

	/**
	 * Returns all valid move indices for a player.
	 * @param {number} player
	 * @param {Array} board
	 * @returns {number[]}
	 */
	getAllValidMoves(player, board) {
		const moves = [];
		for (let i = 0; i < 64; i++) {
			if (this.isValidMoveFor(i, player, board)) moves.push(i);
		}
		return moves;
	}

	/**
	 * Counts empty cells that are permanently guaranteed to the player on the given
	 * board — i.e. cells adjacent only to the player's own pieces and which the
	 * opponent can never reach to destroy.
	 * @param {number} player
	 * @param {Array} board
	 * @returns {number}
	 */
	countGuaranteedCells(player, board) {
		const myPiece = player === 1 ? 'cat' : 'dog';
		const oppPiece = player === 1 ? 'dog' : 'cat';
		let count = 0;
		for (let i = 0; i < 64; i++) {
			if (board[i] !== null) continue;
			const adj = getAdjacentCells(i);
			const hasMyPiece = adj.some(j => board[j] === myPiece);
			const hasOppPiece = adj.some(j => board[j] === oppPiece);
			if (!hasMyPiece || hasOppPiece) continue;
			// Permanently guaranteed only if the opponent can never place an adjacent
			// piece to "destroy" this cell (i.e. every empty neighbor already has a
			// neighbor of mine, blocking the opponent from landing there).
			const canOppDestroy = adj.some(
				j => board[j] === null &&
					!getAdjacentCells(j).some(k => board[k] === myPiece)
			);
			if (!canOppDestroy) count++;
		}
		return count;
	}

	/**
	 * Returns a composite heuristic score for placing at `index`.
	 * Higher scores are better.
	 * @param {number} index
	 * @param {number} player
	 * @param {Array} board
	 * @returns {number}
	 */
	scoreMove(index, player, board) {
		const opponent = player === 1 ? 2 : 1;
		const myPiece = player === 1 ? 'cat' : 'dog';
		const newBoard = [...board];
		newBoard[index] = myPiece;

		let score = 0;

		// a. Maximize guaranteed (permanently owned) cells
		const guaranteedBefore = this.countGuaranteedCells(player, board);
		const guaranteedAfter = this.countGuaranteedCells(player, newBoard);
		score += (guaranteedAfter - guaranteedBefore) * 10;

		// b. Territory expansion: new empty cells adjacent to the placed piece
		const newAdjacencies = getAdjacentCells(index).filter(j => newBoard[j] === null).length;
		score += newAdjacencies * 3;

		// c. Deny opponent expansion: reduce their valid-move count
		const oppMovesBefore = this.getAllValidMoves(opponent, board).length;
		const oppMovesAfter = this.getAllValidMoves(opponent, newBoard).length;
		score += (oppMovesBefore - oppMovesAfter) * 5;

		// d. Avoid placing on already-guaranteed cells (save them for auto-fill)
		// If `index` is itself already permanently guaranteed before this move,
		// the cell would be auto-filled later anyway — prefer expanding elsewhere.
		const adjOfIndex = getAdjacentCells(index);
		const oppPiece = player === 1 ? 'dog' : 'cat';
		const indexAlreadyGuaranteed =
			adjOfIndex.some(j => board[j] === myPiece) &&
			!adjOfIndex.some(j => board[j] === oppPiece) &&
			!adjOfIndex.some(
				j => board[j] === null &&
					!getAdjacentCells(j).some(k => board[k] === myPiece)
			);
		if (indexAlreadyGuaranteed) {
			score -= 5;
		}

		// e. Central positioning bonus during the early game
		const pieceCount = board.filter(c => c !== null).length;
		if (pieceCount < 12) {
			const row = Math.floor(index / 8);
			const col = index % 8;
			const centerDist = Math.abs(row - 3.5) + Math.abs(col - 3.5);
			score += Math.max(0, 5 - centerDist);
		}

		return score;
	}

	/**
	 * Makes a move for the computer player.
	 * @param {Array} board - 64-element array (null | 'cat' | 'dog')
	 * @param {number} player - 1 (cats) or 2 (dogs)
	 * @returns {number|null} - Valid cell index, or null if no move exists
	 */
	makeMove(board, player) {
		const validMoves = this.getAllValidMoves(player, board);
		if (validMoves.length === 0) return null;
		if (validMoves.length === 1) return validMoves[0];

		const opponent = player === 1 ? 2 : 1;
		const myPiece = player === 1 ? 'cat' : 'dog';
		const oppPiece = opponent === 1 ? 'cat' : 'dog';

		// 1. Immediate win: any move that leaves the opponent with zero valid moves
		for (const move of validMoves) {
			const newBoard = [...board];
			newBoard[move] = myPiece;
			if (this.getAllValidMoves(opponent, newBoard).length === 0) {
				return move;
			}
		}

		// 2. Avoid losing moves: filter out moves that hand the opponent an instant win
		const safeMoves = validMoves.filter(move => {
			const newBoard = [...board];
			newBoard[move] = myPiece;
			const opponentMoves = this.getAllValidMoves(opponent, newBoard);
			return !opponentMoves.some(oppMove => {
				const afterOppBoard = [...newBoard];
				afterOppBoard[oppMove] = oppPiece;
				return this.getAllValidMoves(player, afterOppBoard).length === 0;
			});
		});

		const candidates = safeMoves.length > 0 ? safeMoves : validMoves;

		// 3. Score each candidate and pick the best
		const scored = candidates.map(move => ({ move, score: this.scoreMove(move, player, board) }));
		const maxScore = Math.max(...scored.map(s => s.score));
		const best = scored.filter(s => s.score === maxScore);

		// 4. Fallback: random choice among equally-scored moves
		return best[Math.floor(Math.random() * best.length)].move;
	}
}

export default CatsAndDogsComputerPlayer;
